// ============================================
// DASHBOARDDIRECTOR.JS — Panel ejecutivo
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { OPC_DINERO } from './utils/dinero';

const PP = "'Poppins', sans-serif";
const COLOR_PRINCIPAL = '#5c9adb';
const COLOR_OSCURO = '#2f5f9e';
const COLOR_FONDO = '#f3f4f4';
const COLOR_GRIS = '#4e5357';
const COLOR_GRIS_CLARO = '#8a8f95';
const COLOR_VERDE = '#5cd4b0';
const COLOR_ROJO = '#e05c5c';
const COLOR_AMARILLO = '#dcde74';

function leer(key, def) {
  try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : def; } catch { return def; }
}

function fechaCorta(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }); } catch { return '—'; }
}

function esHoy(iso) {
  if (!iso) return false;
  const d = new Date(iso); const h = new Date();
  return d.getDate() === h.getDate() && d.getMonth() === h.getMonth() && d.getFullYear() === h.getFullYear();
}

function DashboardDirector({ rol, seccion, esCelular }) {
  const [tab, setTab] = useState(seccion || 'general');
  const [tick, setTick] = useState(0);

  useEffect(() => { if (seccion) setTab(seccion); }, [seccion]);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // Datos de localStorage
  const servicios = leer('beep_servicios', []);
  const proyectos = leer('beep_proyectos', []);
  const cotizaciones = leer('beep_cotizaciones', []);
  const gastos = leer('beep_gastos', []);
  const tareas = leer('beep_tareas', []);
  const inventario = leer('beep_inventario', []);
  const usuarios = leer('beep_usuarios', []);
  const clientes = leer('beep_clientes', []);

  const tabs = [
    { id: 'general', label: 'Vista general' },
    { id: 'operaciones', label: 'Operaciones' },
    { id: 'comercial', label: 'Comercial' },
    { id: 'finanzas', label: 'Finanzas' },
  ];

  return (
    <div style={{ fontFamily: PP, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{ fontSize: esCelular ? 20 : 24, fontWeight: 800, color: COLOR_GRIS }}>📊 Dashboard ejecutivo</div>
        <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 4 }}>Métricas en tiempo real</div>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '8px 16px', borderRadius: 12,
              background: tab === t.id ? COLOR_PRINCIPAL : '#fff',
              color: tab === t.id ? '#fff' : COLOR_OSCURO,
              border: `1.5px solid ${COLOR_PRINCIPAL}`,
              fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: PP,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'general' && (
        <VistaGeneral servicios={servicios} proyectos={proyectos} cotizaciones={cotizaciones} gastos={gastos} inventario={inventario} esCelular={esCelular} />
      )}
      {tab === 'operaciones' && (
        <VistaOperaciones servicios={servicios} usuarios={usuarios} esCelular={esCelular} />
      )}
      {tab === 'comercial' && (
        <VistaComercial cotizaciones={cotizaciones} clientes={clientes} esCelular={esCelular} />
      )}
      {tab === 'finanzas' && (
        <VistaFinanzas proyectos={proyectos} gastos={gastos} esCelular={esCelular} />
      )}
    </div>
  );
}

// ====== VISTA GENERAL ======
function VistaGeneral({ servicios, proyectos, cotizaciones, gastos, inventario, esCelular }) {
  const serviciosHoy = servicios.filter(s => esHoy(s.fechaProgramada));
  const completados = serviciosHoy.filter(s => ['completado', 'validado', 'facturado'].includes(s.estado)).length;
  const enProceso = serviciosHoy.filter(s => ['en_camino', 'en_sitio', 'en_proceso'].includes(s.estado)).length;
  const pendientes = serviciosHoy.filter(s => ['nuevo', 'asignado'].includes(s.estado)).length;

  const proyectosActivos = proyectos.filter(p => ['aprobado', 'anticipo', 'en_ejecucion'].includes(p.estado));
  const proyectosCotizacion = proyectos.filter(p => p.estado === 'cotizacion');

  const cotEnviadas = cotizaciones.filter(c => c.estado === 'enviada').length;
  const gastosPendientes = gastos.filter(g => g.estado === 'pendiente').length;

  const stockBajo = inventario.filter(a => a.cantidad < a.cantidadMinima);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: esCelular ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 10 }}>
        <KPI titulo="Servicios hoy" valor={serviciosHoy.length} sub={`${completados} completados · ${enProceso} en proceso`} color={COLOR_PRINCIPAL} icono="🛠️" />
        <KPI titulo="Proyectos activos" valor={proyectosActivos.length} sub={`${proyectosCotizacion.length} en cotización`} color={COLOR_VERDE} icono="🏗️" />
        <KPI titulo="Cotizaciones" valor={cotEnviadas} sub="enviadas" color={COLOR_AMARILLO} icono="📝" />
        <KPI titulo="Gastos por aprobar" valor={gastosPendientes} sub="" color={COLOR_ROJO} icono="💳" />
      </div>

      <Card titulo="🚨 Alertas del sistema">
        {pendientes === 0 && stockBajo.length === 0 ? (
          <Vacio>Sin alertas activas 🎉</Vacio>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pendientes > 0 && <Alerta texto={`${pendientes} servicios sin asignar para hoy`} color={COLOR_ROJO} />}
            {stockBajo.slice(0, 3).map(a => (
              <Alerta key={a.id} texto={`Stock bajo: ${a.nombre} (${a.cantidad} ${a.unidad})`} color={COLOR_AMARILLO} />
            ))}
          </div>
        )}
      </Card>

      <Card titulo="📋 Actividad reciente">
        <ActividadReciente servicios={servicios} proyectos={proyectos} />
      </Card>
    </div>
  );
}

// ====== VISTA OPERACIONES ======
function VistaOperaciones({ servicios, usuarios, esCelular }) {
  const porEstado = useMemo(() => {
    const e = {};
    servicios.forEach(s => { e[s.estado] = (e[s.estado] || 0) + 1; });
    return e;
  }, [servicios]);

  const tecnicos = usuarios.filter(u => u.rol === 'tecnico' || u.rol === 'supervisor');
  const max = Math.max(1, ...Object.values(porEstado));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card titulo="Servicios por estado">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Object.entries(porEstado).map(([k, v]) => (
            <div key={k}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: COLOR_GRIS }}>
                <span>{k}</span><span style={{ fontWeight: 700 }}>{v}</span>
              </div>
              <div style={{ background: COLOR_FONDO, height: 8, borderRadius: 4, overflow: 'hidden', marginTop: 2 }}>
                <div style={{ width: `${(v / max) * 100}%`, height: '100%', background: COLOR_PRINCIPAL }} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card titulo="Técnicos">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {tecnicos.map(t => {
            const sus = servicios.filter(s => s.tecnicoAsignado?.id === t.id && esHoy(s.fechaProgramada));
            const enCampo = sus.find(s => ['en_camino', 'en_sitio', 'en_proceso'].includes(s.estado));
            return (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 8, background: COLOR_FONDO, borderRadius: 8 }}>
                <span style={{ fontSize: 14 }}>{enCampo ? '🟢' : '⚪'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLOR_GRIS }}>{t.nombre}</div>
                  <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO }}>{sus.length} servicios hoy</div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, color: enCampo ? COLOR_VERDE : COLOR_GRIS_CLARO }}>
                  {enCampo ? 'En campo' : 'Disponible'}
                </span>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// ====== VISTA COMERCIAL ======
function VistaComercial({ cotizaciones, clientes, esCelular }) {
  const activos = clientes.filter(c => c.estado === 'activo').length;
  const prospectos = clientes.filter(c => c.estado === 'prospecto').length;
  const montoAbierto = cotizaciones
    .filter(c => ['enviada', 'borrador'].includes(c.estado))
    .reduce((s, c) => s + (c.total || 0), 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: esCelular ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: 10 }}>
        <KPI titulo="Clientes activos" valor={activos} color={COLOR_VERDE} icono="🏢" />
        <KPI titulo="Prospectos" valor={prospectos} color={COLOR_AMARILLO} icono="🎯" />
        <KPI titulo="Cotizaciones abiertas" valor={`$${(montoAbierto || 0).toLocaleString('es-MX', OPC_DINERO)}`} color={COLOR_PRINCIPAL} icono="💰" />
      </div>
      <Card titulo="Cotizaciones recientes">
        {cotizaciones.length === 0 ? <Vacio>Sin cotizaciones registradas</Vacio> : (
          cotizaciones.slice(0, 8).map((c, i) => (
            <div key={i} style={{ padding: 8, borderBottom: '1px solid #f0f2f5', fontSize: 12, color: COLOR_GRIS }}>
              <strong>{c.folio || `COT-${i + 1}`}</strong> · {c.cliente || '—'} · ${(c.total || 0).toLocaleString('es-MX', OPC_DINERO)}
            </div>
          ))
        )}
      </Card>
    </div>
  );
}

// ====== VISTA FINANZAS ======
function VistaFinanzas({ proyectos, gastos, esCelular }) {
  const totalProyectos = proyectos.reduce((s, p) => s + (p.montoTotal || 0), 0);
  const anticipoRecibido = proyectos.filter(p => p.anticipoRecibido).reduce((s, p) => s + (p.anticipo80 || 0), 0);
  const anticipoPendiente = proyectos.filter(p => !p.anticipoRecibido && ['aprobado', 'cotizacion'].includes(p.estado)).reduce((s, p) => s + (p.anticipo80 || 0), 0);
  const gastosPend = gastos.filter(g => g.estado === 'pendiente').reduce((s, g) => s + (g.monto || 0), 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: esCelular ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 10 }}>
        <KPI titulo="Total proyectos" valor={`$${totalProyectos.toLocaleString('es-MX', OPC_DINERO)}`} color={COLOR_OSCURO} icono="📐" />
        <KPI titulo="Anticipos recibidos" valor={`$${anticipoRecibido.toLocaleString('es-MX', OPC_DINERO)}`} color={COLOR_VERDE} icono="✓" />
        <KPI titulo="Anticipos pendientes" valor={`$${anticipoPendiente.toLocaleString('es-MX', OPC_DINERO)}`} color={COLOR_AMARILLO} icono="⏳" />
        <KPI titulo="Gastos por reembolsar" valor={`$${gastosPend.toLocaleString('es-MX', OPC_DINERO)}`} color={COLOR_ROJO} icono="💳" />
      </div>
      <Card titulo="Proyectos por estado financiero">
        {proyectos.slice(0, 8).map(p => (
          <div key={p.id} style={{ padding: 10, background: COLOR_FONDO, borderRadius: 8, marginBottom: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span style={{ fontWeight: 700, color: COLOR_GRIS }}>{p.titulo}</span>
              <span style={{ fontWeight: 700, color: COLOR_OSCURO }}>${(p.montoTotal || 0).toLocaleString('es-MX', OPC_DINERO)}</span>
            </div>
            <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
              {p.anticipoRecibido ? '✅ Anticipo' : '⏳ Anticipo pendiente'} ·{' '}
              {p.finiquitoRecibido ? '✅ Finiquito' : '⏳ Finiquito pendiente'}
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ====== HELPERS ======
function KPI({ titulo, valor, sub, color, icono }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: 14,
      boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
      borderLeft: `4px solid ${color}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 700, textTransform: 'uppercase' }}>{titulo}</div>
        <span style={{ fontSize: 18 }}>{icono}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: COLOR_OSCURO, marginTop: 4 }}>{valor}</div>
      {sub && <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Card({ titulo, children }) {
  return (
    <div style={{ background: '#fff', borderRadius: 14, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 10 }}>{titulo}</div>
      {children}
    </div>
  );
}

function Alerta({ texto, color }) {
  return (
    <div style={{ padding: 10, background: color + '15', borderLeft: `3px solid ${color}`, borderRadius: 6, fontSize: 12, color: COLOR_GRIS }}>
      ⚠️ {texto}
    </div>
  );
}

function Vacio({ children }) {
  return <div style={{ padding: 20, textAlign: 'center', color: COLOR_GRIS_CLARO, fontSize: 12 }}>{children}</div>;
}

function ActividadReciente({ servicios, proyectos }) {
  const eventos = [];
  servicios.forEach(s => (s.historial || []).slice(-2).forEach(h => eventos.push({ ...h, ref: s.folio })));
  proyectos.forEach(p => (p.historial || []).slice(-2).forEach(h => eventos.push({ ...h, ref: p.folio })));
  eventos.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  if (eventos.length === 0) return <Vacio>Sin actividad registrada</Vacio>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 240, overflowY: 'auto' }}>
      {eventos.slice(0, 10).map((e, i) => (
        <div key={i} style={{ padding: 8, fontSize: 11, color: COLOR_GRIS, borderBottom: '1px solid #f0f2f5' }}>
          <strong>{e.ref}</strong> · {e.accion}
          <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO }}>{new Date(e.fecha).toLocaleString('es-MX')}</div>
        </div>
      ))}
    </div>
  );
}

export default DashboardDirector;
